import { twMerge } from "tailwind-merge";

type TableDateTimeProps = {
  date: string | null | undefined;
  startTime?: string | null;
  endTime?: string | null;
  dateFormat?: Intl.DateTimeFormatOptions;
  className?: string;
};

const defaultDateFormat: Intl.DateTimeFormatOptions = {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
};

const formatTimeWithAmPm = (timeStr: string): string => {
  if (!timeStr || !timeStr.trim()) return "";
  const [hours24, minutes] = parseTimeTo24(timeStr);
  const d = new Date(2000, 0, 1, hours24, minutes);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Parses a time string ("11:00 AM", "03:00 AM", "15:00") to 24h hours and minutes.
 * Used for building sortable timestamps when date + time are separate strings.
 */
export const parseTimeTo24 = (timeStr: string | null | undefined): [number, number] => {
  if (!timeStr || !timeStr.trim()) {
    return [0, 0];
  }
  const normalized = timeStr.trim().toUpperCase();
  const isPm = normalized.includes("PM");
  const isAm = normalized.includes("AM");
  const numericPart = normalized.replace(/\s*AM\s*$/i, "").replace(/\s*PM\s*$/i, "").trim();
  const [ hourPart, minutePart ] = numericPart.split(":");
  let hour = parseInt(hourPart ?? "0", 10);
  const minute = parseInt((minutePart ?? "00").replace(/\D/g, "").slice(0, 2), 10) || 0;
  if (isPm && hour !== 12) hour += 12;
  if (isAm && hour === 12) hour = 0;
  return [ hour, minute ];
};

/**
 * Returns a numeric timestamp for sorting by date + startTime.
 * Handles API formats: date "5/13/2023" or "2023-05-13", startTime "11:00 AM" or "11:00".
 */
export const getDateTimeSortValue = (dateStr: string | null | undefined, startTimeStr: string | null | undefined): number => {
  if (!dateStr || dateStr === "Unknown date") {
    return 0;
  }
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) {
    return 0;
  }
  const [ hours, minutes ] = parseTimeTo24(startTimeStr);
  d.setHours(hours, minutes, 0, 0);
  return d.getTime();
};

const TableDateTime = ({
  date,
  startTime,
  endTime,
  dateFormat = defaultDateFormat,
  className,
}: TableDateTimeProps) => {
  const hasDate = date && date !== "Unknown date";
  const formattedDate = hasDate
    ? new Date(date).toLocaleDateString("en-US", dateFormat)
    : "—";

  const startFormatted = startTime ? formatTimeWithAmPm(startTime) : "";
  const endFormatted = endTime ? formatTimeWithAmPm(endTime) : "";
  const timeLabel: string | null =
    startFormatted && endFormatted
      ? `${startFormatted} – ${endFormatted}`
    : startFormatted || null;

  return (
    <div className="flex flex-col gap-0.5">
      <span className={twMerge("text-sm font-medium text-gray-900 tabular-nums", className)}>
        {formattedDate}
      </span>
      {timeLabel && (
        <span className="text-xs text-gray-500 tabular-nums">
          {timeLabel}
        </span>
      )}
    </div>
  );
};

export default TableDateTime;
